'use client';

import React, { createContext, useContext, useState, useEffect, startTransition } from 'react';

export type ConsentLevel = 'all' | 'necessary' | null;

interface ConsentContextType {
  consent: ConsentLevel;
  setConsent: (level: ConsentLevel) => void;
  isLoading: boolean;
}

// Exported (not just the usual useConsent()) so a component that renders outside the
// (store) route group's provider tree — e.g. DeferredTanganicaScripts, mounted from the
// root layout on every route — can read it via plain useContext() and treat "no provider"
// as "no consent" instead of crashing (which useConsent()'s throw would do there).
export const ConsentContext = createContext<ConsentContextType | undefined>(undefined);

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
    _gcmInitialized?: boolean;
    Cookiebot?: {
      consent?: {
        necessary: boolean;
        preferences: boolean;
        statistics: boolean;
        marketing: boolean;
      };
      renew?: () => void;
    };
  }
}

export function ConsentProvider({ children }: { children: React.ReactNode }) {
  const [consent, setConsentState] = useState<ConsentLevel>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Initialize dataLayer and gtag for Google Consent Mode v2
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
      window.gtag = function () {
        window.dataLayer?.push(arguments);
      };
    }

    const stored = localStorage.getItem('cookie-consent') as ConsentLevel;
    const initialLevel = (stored === 'all' || stored === 'necessary') ? stored : null;

    if (!window._gcmInitialized) {
      window._gcmInitialized = true;
      const gcmState = initialLevel === 'all' ? 'granted' : 'denied';
      window.gtag('consent', 'default', {
        'ad_storage': gcmState,
        'analytics_storage': gcmState,
        'ad_user_data': gcmState,
        'ad_personalization': gcmState,
        'wait_for_update': 500
      });
    }

    // A transition, not a plain update: this provider wraps every page, and a sync update here
    // reaches a still-dehydrated Suspense boundary (a route's loading.tsx) before it hydrates —
    // React then throws away the server HTML and client-renders it, flashing the loading
    // fallback and remounting every image (measured on product pages: CLS 0.36, LCP reset).
    // Transition updates wait for hydration instead.
    startTransition(() => {
      if (initialLevel) {
        setConsentState(initialLevel);
      }
      setIsLoading(false);
    });
  }, []);

  const setConsent = (level: ConsentLevel) => {
    setConsentState(level);
    if (typeof window !== 'undefined') {
      if (level) {
        localStorage.setItem('cookie-consent', level);
      } else {
        localStorage.removeItem('cookie-consent');
      }

      if (window.gtag) {
        const state = level === 'all' ? 'granted' : 'denied';
        window.gtag('consent', 'update', {
          'ad_storage': state,
          'analytics_storage': state,
          'ad_user_data': state,
          'ad_personalization': state,
        });
      }
    }
  };

  return (
    <ConsentContext.Provider value={{ consent, setConsent, isLoading }}>
      {children}
    </ConsentContext.Provider>
  );
}

const defaultConsentContext: ConsentContextType = {
  consent: null,
  setConsent: () => {},
  isLoading: false,
};

export function useConsent() {
  const context = useContext(ConsentContext);
  return context ?? defaultConsentContext;
}
